import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

const SKILLS = [
  'Inner strength',
  'Emotional control',
  'Communication',
  'Leadership',
  'Productivity',
  'Business thinking',
  'Financial intelligence',
  'Discipline',
  'Identity',
];

const FOUNDATIONS = [
  {
    word: 'Discipline.',
    tail: 'Direction to action — showing up when motivation isn\u2019t there.',
  },
  {
    word: 'Structure.',
    tail: 'Direction to effort — a clear path instead of random content.',
  },
  {
    word: 'Community.',
    tail: 'Strength to consistency — people who hold the same standard.',
  },
];

const Mission = () => {
  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-28 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="mission-eyebrow"
          >
            <Compass className="w-3.5 h-3.5 text-cyan-300" strokeWidth={2.5} />
            Our mission
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Take growth back to{' '}
            <em className="italic font-light text-blue-600">what matters.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Levanta&rsquo;s mission is to make personal growth structured, practical
            and sustainable — replacing motivation-dependent self-help with a clear
            system of discipline, skill-building and long-term development.
          </p>
        </div>
      </section>

      {/* WHAT WE DO — skill cloud */}
      <section className="px-6 py-20 md:py-24 bg-[#f4f8fc]">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4 text-center">
            What we train
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] text-center mb-10">
            Not isolated topics.{' '}
            <em className="italic font-light text-blue-600">Connected skills.</em>
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {SKILLS.map((s) => (
              <span
                key={s}
                className="px-5 py-2.5 rounded-full bg-white text-[#0b1f3a] text-sm md:text-base font-semibold ring-1 ring-black/5 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
              >
                {s}
              </span>
            ))}
            <span className="px-5 py-2.5 rounded-full bg-transparent text-[#0b1f3a]/55 text-sm md:text-base font-medium italic">
              and more…
            </span>
          </div>
          <p className="mt-10 text-center text-[#0b1f3a]/70 max-w-2xl mx-auto leading-relaxed">
            Each one builds the next. Skills compound. So does identity.
          </p>
        </div>
      </section>

      {/* THE THREE NOTS — flowing manifesto prose, not matching blocks. */}
      <section className="px-6 py-24 md:py-32 bg-[#0b1f3a] text-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300 mb-8 text-center">
            What we are not
          </p>
          <div className="space-y-7 text-xl md:text-2xl leading-relaxed text-white/80">
            <p>
              We are not here to{' '}
              <em className="italic font-light text-blue-300">entertain</em> you for an
              afternoon. The world has enough content. What it lacks is a place
              to put what you already know into practice.
            </p>
            <p>
              We are not here to{' '}
              <em className="italic font-light text-blue-300">motivate</em> you for a
              moment. Motivation is a feeling. Feelings come and go with sleep,
              stress, weather, mood. Building your life on top of motivation is
              like building a house on top of fog.
            </p>
            <p>
              We are not here to{' '}
              <em className="italic font-light text-blue-300">sell</em> you a quick
              transformation. Real change is not a 30-day product. It&rsquo;s a
              long, quiet, repeating practice — and the work doesn&rsquo;t look like
              the brochure.
            </p>
          </div>

          <div className="mt-16 text-center">
            <div className="inline-flex items-center gap-3 px-7 py-3 rounded-full bg-white/5 ring-1 ring-white/15">
              <span className="w-2 h-2 rounded-full bg-cyan-300" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-300">
                Our mission is
              </p>
            </div>
            <p className="mt-7 text-4xl md:text-6xl font-black leading-tight tracking-tight">
              To <em className="italic font-light text-blue-300">train</em> growth.
            </p>
          </div>
        </div>
      </section>

      {/* REPLACE & REBUILD — inspirational prose instead of a 4-pair grid. */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5 text-center">
            What we replace
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.08] tracking-tight text-[#0b1f3a] text-center mb-12">
            Replace, then{' '}
            <em className="italic font-light text-blue-600">rebuild.</em>
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-[#0b1f3a]/80 leading-relaxed">
            <p>
              The first job is honest: we replace what doesn&rsquo;t work. We replace
              the chaos of <em>what should I do today</em> with the clarity of a
              path you can actually see. We replace the inconsistency of {' '}
              <em>I&rsquo;ll feel like it tomorrow</em> with a system that doesn&rsquo;t
              ask you how you feel.
            </p>
            <p>
              Then we rebuild. We rebuild isolation into community — people
              who hold the same standard you do, on the days when you can&rsquo;t hold
              it alone. We rebuild information into capability — knowledge that
              doesn&rsquo;t live in your bookmarks but in your behaviour, because
              you&rsquo;ve practiced it long enough that it became you.
            </p>
            <p>
              That&rsquo;s what Levanta is doing. Not adding more content to the pile.
              Replacing the pile with a structure that turns time into growth.
            </p>
          </div>
        </div>
      </section>

      {/* THREE FOUNDATIONS */}
      <section className="px-6 py-20 md:py-24 bg-gradient-to-b from-[#eaf3fb] to-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-5">
            Levanta stands on three foundations
          </p>
          <h2 className="text-3xl md:text-5xl font-black leading-[1.05] tracking-tight text-[#0b1f3a]">
            When these three come together,{' '}
            <em className="italic font-light text-blue-600">growth stops being fragile.</em>
          </h2>
          {/* Desktop: 3-column grid. Mobile: horizontal swipe carousel so
              Discipline / Structure / Community don't take 3 stacked
              cards of scroll on a phone. */}
          <div className="hidden md:grid md:grid-cols-3 gap-5 mt-12">
            {FOUNDATIONS.map((p) => (
              <div
                key={p.word}
                className="rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg text-left"
              >
                <p className="text-3xl md:text-4xl font-black text-[#0b1f3a] mb-3">
                  <em className="italic font-light text-blue-600">{p.word}</em>
                </p>
                <p className="text-[#0b1f3a]/70 leading-relaxed">{p.tail}</p>
              </div>
            ))}
          </div>
          <div
            className="md:hidden mt-10 -mx-6 px-6 flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 text-left"
            style={{ scrollbarWidth: 'none' }}
            data-testid="mission-foundations-mobile"
          >
            {FOUNDATIONS.map((p) => (
              <div
                key={p.word}
                className="shrink-0 w-[80vw] max-w-[320px] snap-start rounded-3xl bg-white ring-1 ring-black/5 shadow-sm p-6"
              >
                <p className="text-2xl font-black text-[#0b1f3a] mb-2">
                  <em className="italic font-light text-blue-600">{p.word}</em>
                </p>
                <p className="text-[#0b1f3a]/70 leading-relaxed text-sm">{p.tail}</p>
              </div>
            ))}
          </div>
          <style>{`
            [data-testid="mission-foundations-mobile"]::-webkit-scrollbar { display: none; }
          `}</style>
          <p className="md:hidden mt-3 text-center text-xs text-[#0b1f3a]/45 tracking-wider uppercase">
            Swipe → to explore
          </p>
        </div>
      </section>

      {/* CLOSING */}
      <section className="px-6 py-24 md:py-28 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl md:text-3xl text-[#0b1f3a]/75 leading-relaxed mb-6">
            We don&rsquo;t just <span className="font-semibold text-[#0b1f3a]">teach</span> growth.
          </p>
          <p className="text-4xl md:text-6xl font-black leading-tight tracking-tight text-[#0b1f3a]">
            We build the structure that makes growth{' '}
            <em className="italic font-light text-blue-600">inevitable.</em>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 md:py-28 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            See where{' '}
            <em className="italic font-light text-blue-300">we&rsquo;re going.</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            Mission is the work today. Vision is the world we&rsquo;re building toward.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/vision"
              data-testid="mission-cta-vision"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Read our vision
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/download-app"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white/5 text-white font-semibold ring-1 ring-white/20 hover:bg-white/10 transition-all duration-200"
            >
              Download Levanta
            </Link>
          </div>
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default Mission;
